import React, { useEffect, useRef, useState } from 'react';
import { Gift, X } from 'lucide-react';
import {
  getAnnouncementStatus,
  recordAnnouncementAction,
  type AnnouncementAction,
  type AnnouncementClient,
} from './client';

export const REFERRAL_PROMO_KEY = 'give50_get50_nov2026';

export interface ReferralPromoPopupProps {
  client: AnnouncementClient | null | undefined;
  /** Signed-in user; nothing is checked or shown while this is empty. */
  userId: string | null | undefined;
  /** Open this app's referral page. */
  onInvite: () => void;
  /** Hostname-style app id stored with the user's choice, e.g. "videomotion". */
  appSource?: string;
  /** Hold the popup back, e.g. while the referral page or another modal is open. */
  suppressed?: boolean;
  delayMs?: number;
}

export const ReferralPromoPopup: React.FC<ReferralPromoPopupProps> = ({
  client,
  userId,
  onInvite,
  appSource,
  suppressed = false,
  delayMs = 4000,
}) => {
  const [open, setOpen] = useState(false);
  const [final, setFinal] = useState(false);
  const checkedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!client || !userId || suppressed || checkedFor.current === userId) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const status = await getAnnouncementStatus(client, REFERRAL_PROMO_KEY);
      if (cancelled) return;
      checkedFor.current = userId;
      if (!status.show) return;
      setFinal(status.final);
      setOpen(true);
      void recordAnnouncementAction(client, REFERRAL_PROMO_KEY, 'shown', appSource);
    }, delayMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [client, userId, suppressed, appSource, delayMs]);

  useEffect(() => {
    if (!userId) setOpen(false);
  }, [userId]);

  const close = (action: AnnouncementAction) => {
    setOpen(false);
    if (client) void recordAnnouncementAction(client, REFERRAL_PROMO_KEY, action, appSource);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close('dismiss');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rehub-referral-promo-title"
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900 shadow-2xl"
      >
        <div
          className="pointer-events-none absolute -top-24 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full bg-orange-500/25 blur-3xl"
          aria-hidden
        />
        <button
          type="button"
          onClick={() => close('dismiss')}
          className="absolute right-3 top-3 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative px-6 pb-6 pt-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-amber-600 shadow-lg shadow-orange-500/30">
            <Gift className="h-7 w-7 text-white" />
          </div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400">
            Bigger referral rewards
          </p>
          <h2 id="rehub-referral-promo-title" className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Give 50, Get 50
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-300">
            Invite a friend to ReHub. They start with 60 credits instead of 10, and you get 50 credits
            when they join.
          </p>

          <div className="mt-5 grid grid-cols-3 gap-2">
            <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 px-2 py-3">
              <p className="text-[10px] uppercase tracking-wider text-orange-300/80">You give</p>
              <p className="text-lg font-bold text-white">+50</p>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-2 py-3">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300/80">They start</p>
              <p className="text-lg font-bold text-white">60</p>
            </div>
            <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-2 py-3">
              <p className="text-[10px] uppercase tracking-wider text-sky-300/80">You get</p>
              <p className="text-lg font-bold text-white">+50</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              close('act');
              onInvite();
            }}
            className="mt-6 w-full rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-600"
          >
            Invite friends
          </button>
          <div className="mt-3 flex items-center justify-center gap-4 text-sm">
            {!final && (
              <button
                type="button"
                onClick={() => close('snooze')}
                className="rounded-lg px-3 py-1.5 font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
              >
                Remind me later
              </button>
            )}
            <button
              type="button"
              onClick={() => close('dismiss')}
              className="rounded-lg px-3 py-1.5 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
            >
              No thanks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
